import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import SearchSelect from '../UI/SearchSelect';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   registerDevice,
   getCredits,
   updateDeviceSetupStatusApi,
   setVlanEnabledApi,
   applyRouterConfig,
   listVlanZonesApi,
   createVlanZonesApi,
   retryVlanZonesApi,
   RegisterDeviceResponse,
} from '@/api/vendo';
import {
   vlanRequiredFor,
   suggestVlanId,
   chosenZoneState,
   shouldPollZones,
   mainRouterOnline,
} from './subVendoVlan';
import type { VendoDevice } from '@/pages/Admin/Vendo';
import NodeMcuFlasher from '../Vendo/NodeMcuFlasher';
import PinMapEditor from '../Vendo/PinMapEditor';
import {
   subVendoDoneView,
   isSubVendoSetupComplete,
   subVendoApplyMode,
} from './subVendoDoneView';

// VLAN port catalog — keep in sync with server/src/utils/routerModels.ts.
// 'trunk' is always available (tagged frames out one port to a managed
// switch). Direct-port slots vary by router model.
const VLAN_PORTS_BY_MODEL: Record<string, string[]> = {
   'hex-rb750gr3': ['ether2', 'ether3', 'ether4', 'ether5'],
   'rb4011': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'sfp-sfpplus1'],
   'ccr2004': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'ether14', 'ether15', 'ether16', 'sfp-sfpplus1', 'sfp-sfpplus2'],
   'ccr2116': ['ether2', 'ether3', 'ether4', 'ether5', 'ether6', 'ether7', 'ether8', 'ether9', 'ether10', 'ether11', 'ether12', 'ether13', 'sfp-sfpplus1', 'sfp-sfpplus2', 'sfp-sfpplus3', 'sfp-sfpplus4'],
};

const MAIN_VENDO_SUBNET_PREFIX = '192.168.88';

// /24 prefixes that must NOT be assigned to a sub-vendo. Mirrors
// RESERVED_SUBNET_24_PREFIXES on the server (utils/routerModels.ts) —
// keep the two lists in sync.
const RESERVED_SUBNET_PREFIXES = [
   '10.10.0', // WireGuard control plane
   '10.10.10', // Comfast WAVE2 default LAN
   '10.0.0', // common pre-configured AP / Comfast LAN
   MAIN_VENDO_SUBNET_PREFIX,
];

const subnetPrefix = (s: string): string =>
   s.split('/')[0].split('.').slice(0, 3).join('.');

const isRfc1918Subnet24 = (s: string): boolean => {
   const m = s.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.0\/24$/);
   if (!m) return false;
   const [, a, b, c] = m.map(Number);
   if (a > 255 || b > 255 || c > 255) return false;
   if (a === 10) return true;
   if (a === 192 && b === 168) return true;
   if (a === 172 && b >= 16 && b <= 31) return true;
   return false;
};

const isReservedSubnet24 = (s: string): boolean =>
   RESERVED_SUBNET_PREFIXES.includes(subnetPrefix(s));

const nextDefaultVlanId = (used: number[]): number => {
   const set = new Set(used);
   for (let n = 20; n <= 4094; n += 10) if (!set.has(n)) return n;
   for (let n = 2; n <= 4094; n++) if (!set.has(n)) return n;
   return 20;
};

// Default sub-vendo subnet: 10.<n>.0.0/24. Stride 10, n ∈ {20..240},
// skipping reserved prefixes. Old 192.168.<n> defaults collided with
// Comfast/TP-Link/Asus downstream APs; existing sub-vendos on the old
// scheme are NOT auto-renumbered.
const nextDefaultSubnet24 = (usedPrefixes: string[]): string => {
   const set = new Set([...RESERVED_SUBNET_PREFIXES, ...usedPrefixes]);
   for (let n = 20; n <= 240; n += 10) {
      const candidate = `10.${n}.0`;
      if (!set.has(candidate)) return `${candidate}.0/24`;
   }
   return '10.20.0.0/24';
};

interface Props {
   // `data` is the PARENT main-vendo. When `_resumeForSubId` is also
   // present, this is a Resume open — the named sub-vendo is already
   // registered (Step 1 was completed in a previous session) and we
   // should jump straight to Step 2 (NodeMCU flashing).
   data: VendoDevice & { _resumeForSubId?: string };
}

type SubForm = {
   name: string;
   nodeMcuMac: string;
};

type VlanChoice = 'pending' | 'yes' | 'no';

type VlanForm = {
   id: string; // string so the input is controlled; parsed at submit
   subnet: string;
   port: string;
};

const MAC_REGEX = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/;

// Sub-vendo registration gate. Flip to true to re-render the
// "coming soon" placeholder if the flow needs to be paused again.
const SUB_VENDO_DISABLED = false;

const SubVendoAdd = ({ data: parent }: Props) => {
   if (SUB_VENDO_DISABLED) {
      return <SubVendoComingSoon parentName={parent?.name || 'this vendo'} />;
   }
   return (
      <SubVendoAddInner
         parent={parent}
         resumeForSubId={parent._resumeForSubId}
      />
   );
};

const SubVendoComingSoon = ({ parentName }: { parentName: string }) => {
   const { handleClose } = useUIStore();
   return (
      <ComingSoonWrap>
         <ComingSoonBadge>Coming soon</ComingSoonBadge>
         <ComingSoonTitle>Sub-vendo registration is paused</ComingSoonTitle>
         <ComingSoonBody>
            We&apos;re reworking how sub-vendos pair with{' '}
            <strong>{parentName}</strong>. The flow will return in a future
            update — your existing sub-vendos keep running normally.
         </ComingSoonBody>
         <ComingSoonActions>
            <Btn
               title="Got it"
               type="normal"
               size="small"
               bgcolor={colors.PRIMARY}
               textcolor={colors.WHITE}
               onClick={handleClose}
            />
         </ComingSoonActions>
      </ComingSoonWrap>
   );
};

type SubStep = 1 | 2 | 3;

const SUB_STEPS: { num: SubStep; label: string }[] = [
   { num: 1, label: 'Register' },
   { num: 2, label: 'Flash NodeMCU' },
   { num: 3, label: 'Done' },
];

const SubVendoAddInner = ({
   parent,
   resumeForSubId,
}: {
   parent: VendoDevice;
   resumeForSubId?: string;
}) => {
   const { handleClose } = useUIStore();
   const navigate = useNavigate();
   const queryClient = useQueryClient();

   // Resume mode: pull the in-progress sub from the devices store and
   // seed `registered` so Step 2 (NodeMCU flasher) can target its _id
   // without re-registering. The store fetch happened in openResume
   // right before this modal mounted, so the lookup should hit.
   const resumeSub = resumeForSubId
      ? useVendoDevicesStore
           .getState()
           .devices.find((d) => d._id === resumeForSubId)
      : null;

   const [step, setStep] = useState<SubStep>(resumeSub ? 2 : 1);
   const [registered, setRegistered] = useState<RegisterDeviceResponse | null>(
      resumeSub
         ? ({
              device: { _id: resumeSub._id, name: resumeSub.name } as any,
              credits: 0,
           } as unknown as RegisterDeviceResponse)
         : null
   );
   const { data: creditsData } = useQuery({
      queryKey: ['credits'],
      queryFn: getCredits,
   });
   const credits = creditsData?.credits ?? 0;
   const hasCredits = credits > 0;

   // Always a 14-day trial (starts on first heartbeat, server-side, same as a
   // main vendo). Auto-renew controls whether 1 credit is auto-charged at trial
   // end. Default ON when the owner already has credit. Replaces the old
   // "use 1 credit now / get-license" choice.
   const [autoRenew, setAutoRenew] = useState<boolean>(credits > 0);
   const [showAdvanced, setShowAdvanced] = useState(false);
   const [form, setForm] = useState<SubForm>({
      name: '',
      nodeMcuMac: '',
   });
   const [errors, setErrors] = useState<{ name?: boolean; nodeMcuMac?: boolean; vlanId?: boolean; vlanSubnet?: boolean; vlanPort?: boolean; vlanRef?: boolean; wifiSsid?: boolean }>({});
   const [isLoading, setIsLoading] = useState(false);

   // VLAN state. Read the raw parent + sibling list straight from the
   // devices store — the display VendoDevice type doesn't carry VLAN
   // fields but the store keeps the raw ApiVendoDevice list.
   const allDevices = useVendoDevicesStore((s) => s.devices);
   const parentRaw = allDevices.find((d) => d._id === parent._id);
   const siblings = allDevices.filter((d) => d.parent === parent._id);
   const vlanAlreadyEnabled = parentRaw?.router?.vlanEnabled === true;
   const isFirstSub = siblings.length === 0;
   const parentRouterModel = parentRaw?.router?.model || 'hex-rb750gr3';
   const availablePorts = VLAN_PORTS_BY_MODEL[parentRouterModel] || VLAN_PORTS_BY_MODEL['hex-rb750gr3'];

   // First sub on a flat-LAN parent gets to make the one-time call.
   // Subsequent subs (or any sub on a VLAN-enabled parent) skip the
   // question — they MUST supply VLAN fields.
   const [vlanChoice, setVlanChoice] = useState<VlanChoice>(
      vlanAlreadyEnabled ? 'yes' : 'pending'
   );

   const usedVlanIds = siblings
      .map((s) => s.router?.vlan?.id)
      .filter((n): n is number => typeof n === 'number');
   const usedSubnetPrefixes = siblings
      .map((s) => s.router?.vlan?.subnet)
      .filter((s): s is string => typeof s === 'string')
      .map(subnetPrefix);

   const [vlanForm, setVlanForm] = useState<VlanForm>(() => ({
      id: String(nextDefaultVlanId(usedVlanIds)),
      subnet: nextDefaultSubnet24(usedSubnetPrefixes),
      port: availablePorts[0] || 'ether2',
   }));

   const needsVlanFields = vlanAlreadyEnabled || vlanChoice === 'yes';

   // ---- VLAN redesign (2026-05-31) -------------------------------------
   // New parents carry reusable VLAN "zones" (router.vlans[]). A sub attaches
   // to one FREE zone via vlanRef — no per-sub id/subnet/port entry, no
   // one-time isolate toggle (zones are created on the Networks page). The
   // legacy vlanEnabled path above is left intact for grandfathered clusters.
   const { data: zonesData } = useQuery({
      queryKey: ['vlan-zones', parent._id],
      queryFn: () => listVlanZonesApi(parent._id),
      enabled: !vlanAlreadyEnabled, // legacy parents don't use zones
      // Keep reading while a free zone is still turning on, so a VLAN created
      // from this wizard goes green here without a refresh.
      refetchInterval: (q) => (shouldPollZones(q.state.data?.zones ?? []) ? 3000 : false),
   });
   const zones = zonesData?.zones ?? [];
   const parentHasZones = !vlanAlreadyEnabled && zones.length > 0;
   // Which kind of router the parent is. An OpenWRT (OrangePi) parent is never
   // pushed to — it reads the layout on its next heartbeat — so the wizard
   // must not call the MikroTik apply at it, nor show MikroTik wording.
   const parentPlatform: 'mikrotik' | 'openwrt' | undefined =
      zonesData?.platform ?? parentRaw?.router?.platform;
   const parentIsOpenwrt = parentPlatform === 'openwrt';
   // Only LIVE, unattached zones can take a sub — a pending/failed zone isn't
   // actually on the router yet, so attaching to it would orphan the sub.
   const freeZones = zones.filter((z) => !z.attachedSub && z.status === 'live');
   // Free zones that exist but aren't live yet (still pushing) — used to show a
   // "wait for them to finish" hint instead of "all zones in use".
   const freeNotLiveCount = zones.filter(
      (z) => !z.attachedSub && z.status !== 'live'
   ).length;
   const [vlanRef, setVlanRef] = useState<number | ''>('');
   // NodeMCU connection mode for the sub. Same chip/firmware/flash as the main
   // vendo, so wireless works identically (creds baked into the flash). Default
   // wired to avoid disrupting the existing wired-sub VLAN/port flow; the
   // operator picks Wi-Fi to go LAN-module-free.
   const [connMode, setConnMode] = useState<'ethernet' | 'wifi'>('ethernet');
   const [wifiSsid, setWifiSsid] = useState('');
   const [wifiPassword, setWifiPassword] = useState('');

   // Default the zone picker to the first free zone once zones load.
   useEffect(() => {
      if (parentHasZones && vlanRef === '' && freeZones.length) {
         setVlanRef(freeZones[0].vlanId);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [parentHasZones, freeZones.length]);

   // ---- VLAN required (Ray, 2026-10-15) --------------------------------
   // On a main that can host VLANs a sub is never registered without one. When
   // no free VLAN is found, the "VLAN zone" card creates it right here; Next
   // stays locked until the router has it, and while the main is offline.
   const vlanRequired = vlanRequiredFor(parentRaw?.router);
   const mainOnline = mainRouterOnline(parentRaw as { routerStatus?: string; status?: string });
   const zoneState = chosenZoneState(zones, vlanRef);
   const vlanReady = !vlanRequired || (mainOnline && zoneState === 'live');
   const takenVlanIds = [
      ...zones.map((z) => z.vlanId),
      ...(parentRaw?.router?.lanIsolation && typeof parentRaw.router.mainVlanId === 'number'
         ? [parentRaw.router.mainVlanId]
         : []),
      ...(typeof parentRaw?.router?.wan?.vlanId === 'number' ? [parentRaw.router.wan.vlanId] : []),
   ];
   const [newVlanId, setNewVlanId] = useState('');
   const [newVlanTouched, setNewVlanTouched] = useState(false);
   const [creatingVlan, setCreatingVlan] = useState(false);
   const [newVlanErr, setNewVlanErr] = useState('');
   const [createdVlanId, setCreatedVlanId] = useState<number | null>(null);
   const [wantNewVlan, setWantNewVlan] = useState(false);
   const newVlanValue = newVlanTouched ? newVlanId : String(suggestVlanId(takenVlanIds));

   // Fresh online state on open, then every 10 s while the main is offline.
   useEffect(() => {
      if (vlanRequired) void useVendoDevicesStore.getState().fetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   useEffect(() => {
      if (!vlanRequired || mainOnline || step !== 1) return;
      const t = setInterval(() => void useVendoDevicesStore.getState().fetch(), 10000);
      return () => clearInterval(t);
   }, [vlanRequired, mainOnline, step]);

   const createVlan = async () => {
      if (creatingVlan || !mainOnline) return;
      setNewVlanErr('');
      const v = Number(newVlanValue.trim());
      if (!Number.isInteger(v) || v < 10 || v > 4094) {
         setNewVlanErr('VLAN number must be 10–4094.');
         return;
      }
      if (takenVlanIds.includes(v)) {
         setNewVlanErr(`VLAN ${v} is already used on ${parent.name}.`);
         return;
      }
      setCreatingVlan(true);
      try {
         await createVlanZonesApi(parent._id, { vlanId: v });
         setVlanRef(v);
         setCreatedVlanId(v);
         setWantNewVlan(false);
         await queryClient.invalidateQueries({ queryKey: ['vlan-zones', parent._id] });
      } catch (e: any) {
         setNewVlanErr(e?.response?.data?.error || 'Could not create the VLAN.');
      } finally {
         setCreatingVlan(false);
      }
   };

   const retryVlan = async () => {
      try {
         await retryVlanZonesApi(parent._id);
         await queryClient.invalidateQueries({ queryKey: ['vlan-zones', parent._id] });
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not resend the VLAN to the router.');
      }
   };

   const update = <K extends keyof SubForm>(key: K, value: SubForm[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      if (errors[key as keyof typeof errors]) {
         setErrors((prev) => ({ ...prev, [key]: false }));
      }
   };

   const handleSubmit = async () => {
      if (isLoading) return;
      if (!vlanReady) {
         toast.error(
            !mainOnline
               ? `${parent.name} is offline — bring it back online first.`
               : zoneState === 'pending'
               ? 'Wait until the VLAN is live on the router.'
               : 'Pick or create a VLAN for this sub-vendo first.'
         );
         return;
      }

      const nextErrors: typeof errors = {};
      if (!form.name.trim()) nextErrors.name = true;
      if (form.nodeMcuMac.trim() && !MAC_REGEX.test(form.nodeMcuMac.trim())) {
         nextErrors.nodeMcuMac = true;
      }

      // VLAN validation — only when the sub will actually be VLAN-tagged.
      let parsedVlanId: number | null = null;
      if (needsVlanFields) {
         parsedVlanId = parseInt(vlanForm.id, 10);
         if (!Number.isInteger(parsedVlanId) || parsedVlanId < 2 || parsedVlanId > 4094) {
            nextErrors.vlanId = true;
         } else if (usedVlanIds.includes(parsedVlanId)) {
            nextErrors.vlanId = true;
         }
         if (!isRfc1918Subnet24(vlanForm.subnet)) {
            nextErrors.vlanSubnet = true;
         } else if (isReservedSubnet24(vlanForm.subnet)) {
            nextErrors.vlanSubnet = true;
         } else if (usedSubnetPrefixes.includes(subnetPrefix(vlanForm.subnet))) {
            nextErrors.vlanSubnet = true;
         }
         if (
            vlanForm.port !== 'trunk' &&
            !availablePorts.includes(vlanForm.port)
         ) {
            nextErrors.vlanPort = true;
         }
      }

      // VLAN redesign: zoned parent → a free zone must be picked.
      if (parentHasZones && vlanRef === '') {
         nextErrors.vlanRef = true;
      }
      // Wireless NodeMCU needs an SSID (password optional for open networks).
      if (connMode === 'wifi' && !wifiSsid.trim()) {
         nextErrors.wifiSsid = true;
      }

      if (Object.keys(nextErrors).length > 0) {
         setErrors(nextErrors);
         const subnetReserved =
            nextErrors.vlanSubnet &&
            isRfc1918Subnet24(vlanForm.subnet) &&
            isReservedSubnet24(vlanForm.subnet);
         const subnetOverlapping =
            nextErrors.vlanSubnet &&
            isRfc1918Subnet24(vlanForm.subnet) &&
            !isReservedSubnet24(vlanForm.subnet) &&
            usedSubnetPrefixes.includes(subnetPrefix(vlanForm.subnet));
         const msg = nextErrors.vlanRef
            ? 'Pick a VLAN zone for this sub-vendo'
            : nextErrors.wifiSsid
            ? 'Wi-Fi SSID is required for a wireless NodeMCU'
            : nextErrors.vlanId
            ? 'VLAN ID must be 2–4094 and unique'
            : subnetReserved
            ? 'That subnet is reserved (WireGuard or common AP default). Try 10.20.0.0/24.'
            : subnetOverlapping
            ? 'That subnet is already used by another sub-vendo. Try another /24.'
            : nextErrors.vlanSubnet
            ? 'Subnet must start with 10., 172.16–31., or 192.168. — try 10.20.0.0/24'
            : nextErrors.vlanPort
            ? 'Pick a valid port'
            : nextErrors.nodeMcuMac
            ? 'NodeMCU MAC must be in AA:BB:CC:DD:EE:FF format'
            : 'Sub-vendo name is required';
         toast.error(msg);
         return;
      }

      setIsLoading(true);
      try {
         // If this is the first sub AND the operator answered Yes, flip
         // the parent's vlanEnabled BEFORE registering the sub — the
         // registerDevice server-side check will otherwise reject the
         // sub for not having vlan fields under a flat-LAN parent.
         if (isFirstSub && vlanChoice === 'yes' && !vlanAlreadyEnabled) {
            try {
               await setVlanEnabledApi(parent._id, true);
            } catch (vlanErr: any) {
               toast.error(
                  vlanErr?.response?.data?.error ||
                     'Failed to enable VLAN on the parent vendo'
               );
               setIsLoading(false);
               return;
            }
         }

         const macTrim = form.nodeMcuMac.trim();
         const nodeMcuPayload =
            macTrim || connMode === 'wifi'
               ? {
                    nodeMcu: {
                       ...(macTrim ? { mac: macTrim.toUpperCase() } : {}),
                       connMode,
                       ...(connMode === 'wifi'
                          ? {
                               wifiSsid: wifiSsid.trim(),
                               ...(wifiPassword ? { wifiPassword } : {}),
                            }
                          : {}),
                    },
                 }
               : {};

         const res = await registerDevice({
            name: form.name.trim(),
            parent: parent._id,
            // Legacy vlanEnabled parents still take per-sub router.vlan fields.
            ...(needsVlanFields && parsedVlanId !== null
               ? {
                    router: {
                       vlan: {
                          id: parsedVlanId,
                          subnet: vlanForm.subnet,
                          port: vlanForm.port,
                       },
                       backhaul: 'wired',
                    },
                 }
               : {}),
            // Redesign: attach to a parent VLAN zone.
            ...(parentHasZones && vlanRef !== '' ? { vlanRef: Number(vlanRef) } : {}),
            // Always trial-first (same as a main vendo). Auto-renew decides
            // whether the license-expiry cron auto-charges 1 credit at trial
            // end. The 14-day trial itself starts server-side on first
            // heartbeat — no explicit activateTrial call needed.
            autoRenewLicense: autoRenew,
            ...nodeMcuPayload,
         });

         toast.success(
            `Sub-vendo "${res.device.name}" added under ${parent.name}`,
            { autoClose: 2500 }
         );
         queryClient.invalidateQueries({ queryKey: ['credits'] });

         // Do NOT mark the sub 'done' here. It becomes 'done' only when setup
         // is genuinely complete — the router push succeeded (VLAN subs) or no
         // push was needed (flat-LAN / dev). Until then it stays in "Setup in
         // progress" so a wizard closed midway, OR a failed push, can be resumed
         // from the Devices page. (See the step-3 effect below.)
         void useVendoDevicesStore.getState().fetch();

         setRegistered(res);
         setStep(2);
      } catch (err: any) {
         const status = err?.response?.status;
         const msg =
            status === 409
               ? err?.response?.data?.error ||
                 `Parent has reached its sub-vendo cap.`
               : err?.response?.data?.error || 'Failed to add sub-vendo';
         toast.error(msg);
      } finally {
         setIsLoading(false);
      }
   };

   const submitTitle = isLoading ? 'Adding…' : 'Next →';

   // Modal grows WIDE (not tall) when VLAN UI is present. Without this
   // the stacked VLAN sections push the Cancel/Submit buttons below the
   // fold on a typical laptop viewport.
   const showVlanColumn = vlanAlreadyEnabled || parentHasZones || vlanRequired;

   const nodeMcuLink = (
      <>
                     <FieldLabel style={{ marginTop: 12 }}>NodeMCU link</FieldLabel>
                     <VlanChoiceRow>
                        <VlanChoiceBtn
                           type="button"
                           $selected={connMode === 'ethernet'}
                           onClick={() => setConnMode('ethernet')}
                        >
                           Wired
                        </VlanChoiceBtn>
                        <VlanChoiceBtn
                           type="button"
                           $selected={connMode === 'wifi'}
                           onClick={() => setConnMode('wifi')}
                        >
                           Wi-Fi
                        </VlanChoiceBtn>
                     </VlanChoiceRow>
                     {connMode === 'wifi' && (
                        <>
                           <Input
                              label="WiFi name (SSID) *"
                              name="wifiSsid"
                              value={wifiSsid}
                              placeholder="your AP's exact name"
                              error={errors.wifiSsid}
                              onChange={(e) => {
                                 setWifiSsid(e.target.value);
                                 if (errors.wifiSsid)
                                    setErrors((p) => ({ ...p, wifiSsid: false }));
                              }}
                           />
                           <Input
                              label="Wi-Fi password"
                              name="wifiPassword"
                              value={wifiPassword}
                              placeholder="blank if open"
                              onChange={(e) => setWifiPassword(e.target.value)}
                           />
                           <VlanSwitchNotice>
                              The NodeMCU joins this WiFi — the name must match your
                              access point exactly (blank password = open). It&apos;s baked
                              into the chip when you flash it.
                           </VlanSwitchNotice>
                        </>
                     )}
      </>
   );

   const chosenZone = zones.find((z) => z.vlanId === vlanRef);
   // The "VLAN zone" card for a main that requires a VLAN: pick a free one, or
   // create one when none is found.
   const renderVlanCard = () => (
      <VlanSection>
         <VlanLabel>VLAN zone</VlanLabel>
         {!mainOnline ? (
            <OfflineNotice role="alert">
               <strong>{parent.name} is offline.</strong> A VLAN can only be created
               while the main router is online. Check its power and internet — this
               unlocks by itself when it is back.
            </OfflineNotice>
         ) : !zonesData ? (
            <SkeletonLines lines={3} />
         ) : createdVlanId !== null && !chosenZone ? (
            <VlanHelp>Sending VLAN {createdVlanId} to the router…</VlanHelp>
         ) : createdVlanId !== null && chosenZone ? (
            <>
               <ChosenVlan>
                  <strong>VLAN {chosenZone.vlanId}</strong> · {chosenZone.subnet} ·{' '}
                  <ZoneStatus $status={chosenZone.status}>
                     {chosenZone.status === 'live'
                        ? 'Live'
                        : chosenZone.status === 'failed'
                        ? 'Router did not take it'
                        : parentIsOpenwrt
                        ? 'Waiting for the router check-in…'
                        : 'Sending to the router…'}
                  </ZoneStatus>
               </ChosenVlan>
               {chosenZone.status === 'failed' && (
                  <VlanSwitchNotice>
                     Check that {parent.name} is online, then{' '}
                     <LinkBtn type="button" onClick={retryVlan}>
                        send it again
                     </LinkBtn>
                     .
                  </VlanSwitchNotice>
               )}
               {chosenZone.status === 'live' && nodeMcuLink}
            </>
         ) : freeZones.length > 0 && !wantNewVlan ? (
            <>
               <FieldLabel>Zone *</FieldLabel>
               <SearchSelect
                  value={vlanRef === '' ? '' : String(vlanRef)}
                  error={!!errors.vlanRef}
                  placeholder="Select a VLAN zone"
                  searchPlaceholder="Search VLAN / subnet…"
                  emptyText="No matching zone"
                  options={freeZones.map((z) => ({
                     value: String(z.vlanId),
                     label: `VLAN ${z.vlanId}${z.label ? ` · ${z.label}` : ''}`,
                     sublabel: z.subnet,
                  }))}
                  onChange={(v) => {
                     setVlanRef(Number(v));
                     if (errors.vlanRef) setErrors((p) => ({ ...p, vlanRef: false }));
                  }}
               />
               <LinkBtn type="button" onClick={() => setWantNewVlan(true)}>
                  + Create a new VLAN instead
               </LinkBtn>
               {nodeMcuLink}
            </>
         ) : (
            <>
               <VlanHelp>
                  {freeZones.length > 0
                     ? 'Create a new VLAN for this sub-vendo.'
                     : `No free VLAN on ${parent.name} — create one for this sub-vendo.`}{' '}
                  It is sent to the router right away; the subnet is set for you.
               </VlanHelp>
               <NewVlanRow>
                  <Input
                     label="VLAN number (10–4094)"
                     name="newVlanId"
                     value={newVlanValue}
                     placeholder="22"
                     error={!!newVlanErr}
                     onChange={(e) => {
                        setNewVlanTouched(true);
                        setNewVlanId(e.target.value);
                        if (newVlanErr) setNewVlanErr('');
                     }}
                  />
                  <div onClick={createVlan}>
                     <Btn
                        title={creatingVlan ? 'Creating…' : 'Create VLAN'}
                        type={creatingVlan ? 'disabled' : 'outline'}
                        size="small"
                     />
                  </div>
               </NewVlanRow>
               {newVlanErr && <ErrText role="alert">{newVlanErr}</ErrText>}
               {freeZones.length > 0 && (
                  <LinkBtn type="button" onClick={() => setWantNewVlan(false)}>
                     ← Pick a free VLAN instead
                  </LinkBtn>
               )}
            </>
         )}
      </VlanSection>
   );

   const renderStep1 = () => (
      <>
         <Step1Body $twoCol={showVlanColumn}>
            <ColLeft>
               <ParentBanner>
                  <BannerLabel>Parent Main Vendo</BannerLabel>
                  <BannerValue>
                     {parent.name} <ParentId>({parent.id})</ParentId>
                  </BannerValue>
                  <BannerMeta>
                     Router: {parent.router}
                     {parentIsOpenwrt && ' · OpenWRT'}
                  </BannerMeta>
               </ParentBanner>

               <LicenseSection>
                  <SectionLabel>License</SectionLabel>
                  <ChoiceCard type="button" $selected $static>
                     <ChoiceIcon>⏱</ChoiceIcon>
                     <ChoiceTitle>14-Day Trial</ChoiceTitle>
                     <ChoiceDesc>
                        Starts automatically when this sub-vendo first comes online.
                     </ChoiceDesc>
                     <ChoiceTag $green>Free</ChoiceTag>
                  </ChoiceCard>
                  <AutoRenewRow>
                     <AutoRenewCheckbox
                        type="checkbox"
                        id="sub-autoRenew"
                        checked={autoRenew}
                        onChange={(e) => setAutoRenew(e.target.checked)}
                     />
                     <AutoRenewLabel htmlFor="sub-autoRenew">
                        <AutoRenewTitle>Auto-activate — once you have credit</AutoRenewTitle>
                        <AutoRenewDesc>
                           {autoRenew && !hasCredits
                              ? 'Buy credits below — we will auto-charge 1 credit when the trial ends.'
                              : autoRenew
                              ? 'We will use 1 credit automatically when the trial ends — a lifetime license.'
                              : 'Trial ends after 14 days — portal will lock until you activate.'}
                        </AutoRenewDesc>
                     </AutoRenewLabel>
                  </AutoRenewRow>
                  {autoRenew && !hasCredits && (
                     <BuyCreditsCard
                        type="button"
                        onClick={() => {
                           handleClose();
                           navigate('/wifi-partners');
                        }}
                     >
                        <BuyCreditsIcon>🛒</BuyCreditsIcon>
                        <BuyCreditsText>
                           <BuyCreditsTitle>0 credits in wallet — buy now</BuyCreditsTitle>
                           <BuyCreditsDesc>Browse partners selling credits</BuyCreditsDesc>
                        </BuyCreditsText>
                        <BuyCreditsArrow>→</BuyCreditsArrow>
                     </BuyCreditsCard>
                  )}
               </LicenseSection>

               <Input
                  label="Sub-vendo name *"
                  name="name"
                  value={form.name}
                  placeholder="e.g. Kiosk Lobby, Back Entrance"
                  error={errors.name}
                  onChange={(e) => update('name', e.target.value)}
               />

               <AdvancedToggle
                  type="button"
                  onClick={() => setShowAdvanced((v) => !v)}
               >
                  Advanced settings {showAdvanced ? '▴' : '▾'}
               </AdvancedToggle>

               {showAdvanced && (
                  <AdvancedPane>
                     <Input
                        label="NodeMCU MAC"
                        name="nodeMcuMac"
                        value={form.nodeMcuMac}
                        placeholder="CC:50:E3:51:50:BE"
                        error={errors.nodeMcuMac}
                        onChange={(e) => update('nodeMcuMac', e.target.value)}
                     />
                     <AdvancedNote>
                        MAC is captured automatically by the SmartFi flasher in the production
                        flow. Override only if you're registering hardware whose MAC the flasher
                        couldn't capture.
                     </AdvancedNote>
                  </AdvancedPane>
               )}

               {!vlanRequired && !parentHasZones && !vlanAlreadyEnabled && (
                  <Note>
                     This sub will share the main vendo's network. To put it on its
                     own isolated network, create a VLAN zone on the{' '}
                     <strong>Networks</strong> page first, then add the sub-vendo.
                  </Note>
               )}
            </ColLeft>

            {showVlanColumn && (
               <ColRight>
         {vlanRequired && renderVlanCard()}
         {!vlanRequired && parentHasZones && (
            <VlanSection>
               <VlanLabel>VLAN zone</VlanLabel>
               <VlanHelp>
                  Attach this sub-vendo to one of <strong>{parent.name}</strong>&apos;s
                  VLAN zones. Each zone is one isolated network on its own subnet
                  (one zone per sub-vendo). Manage zones on the Networks page.
               </VlanHelp>
               {freeZones.length === 0 ? (
                  <VlanSwitchNotice>
                     {freeNotLiveCount > 0 ? (
                        <>
                           {freeNotLiveCount} VLAN zone(s) are still turning on
                           (pending) on the router. Wait until they show as live on
                           the <strong>Networks</strong> page, then come back.
                        </>
                     ) : (
                        <>
                           All VLAN zones on this vendo are already in use. Create
                           another zone on the <strong>Networks</strong> page, then
                           come back.
                        </>
                     )}
                  </VlanSwitchNotice>
               ) : (
                  <>
                     <FieldLabel>Zone *</FieldLabel>
                     <SearchSelect
                        value={vlanRef === '' ? '' : String(vlanRef)}
                        error={!!errors.vlanRef}
                        placeholder="Select a VLAN zone"
                        searchPlaceholder="Search VLAN / subnet…"
                        emptyText="No matching zone"
                        options={freeZones.map((z) => ({
                           value: String(z.vlanId),
                           label: `VLAN ${z.vlanId}${z.label ? ` · ${z.label}` : ''}`,
                           sublabel: z.subnet,
                        }))}
                        onChange={(v) => {
                           setVlanRef(Number(v));
                           if (errors.vlanRef)
                              setErrors((p) => ({ ...p, vlanRef: false }));
                        }}
                     />

                     {nodeMcuLink}
                  </>
               )}
            </VlanSection>
         )}

         {needsVlanFields && (
            <VlanFields>
               <VlanFieldsTitle>
                  VLAN settings
                  {vlanAlreadyEnabled && (
                     <VlanLockedBadge>Locked — parent has VLAN on</VlanLockedBadge>
                  )}
               </VlanFieldsTitle>
               <Input
                  label="VLAN ID (2–4094) *"
                  name="vlanId"
                  value={vlanForm.id}
                  placeholder="20"
                  error={errors.vlanId}
                  onChange={(e) => {
                     setVlanForm((v) => ({ ...v, id: e.target.value }));
                     if (errors.vlanId) setErrors((p) => ({ ...p, vlanId: false }));
                  }}
               />
               <Input
                  label="Subnet (CIDR /24) *"
                  name="vlanSubnet"
                  value={vlanForm.subnet}
                  placeholder="10.20.0.0/24"
                  error={errors.vlanSubnet}
                  onChange={(e) => {
                     setVlanForm((v) => ({ ...v, subnet: e.target.value }));
                     if (errors.vlanSubnet) setErrors((p) => ({ ...p, vlanSubnet: false }));
                  }}
               />
               <FieldLabel>Sub-vendo port *</FieldLabel>
               <Select
                  value={vlanForm.port}
                  $error={!!errors.vlanPort}
                  onChange={(e) => {
                     setVlanForm((v) => ({ ...v, port: e.target.value }));
                     if (errors.vlanPort) setErrors((p) => ({ ...p, vlanPort: false }));
                  }}
               >
                  {availablePorts.map((p) => (
                     <option key={p} value={p}>
                        Direct port — {p}
                     </option>
                  ))}
                  <option value="trunk">Trunk to managed switch</option>
               </Select>
               {vlanForm.port === 'trunk' && (
                  <VlanSwitchNotice>
                     Your switch must be VLAN-capable (managed).
                     <br />
                     Recommended: <strong>MikroTik CSS610-8G-2S+IN</strong>.
                  </VlanSwitchNotice>
               )}
            </VlanFields>
         )}
               </ColRight>
            )}
         </Step1Body>

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={handleSubmit}>
               <Btn
                  title={vlanRequired && !mainOnline ? 'Router offline' : submitTitle}
                  type={isLoading || !vlanReady ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={isLoading || !vlanReady ? colors.GRAY10 : colors.PRIMARY}
                  textcolor={isLoading || !vlanReady ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      </>
   );

   // Auto-push v2 router script to the PARENT main vendo when VLAN is in
   // play. Without this the operator has to remember to click Update on
   // /admin-panel-vendo, otherwise the new VLAN sub's network never gets
   // configured on the hEX (sub is in DB but has no VLAN interface, no
   // DHCP pool, no hotspot — customers connecting to its AP get nothing).
   //
   // Only fires for VLAN paths. Flat-LAN subs need no script regen (they
   // ride on the parent's existing bridge + hotspot).
   //
   // Skipped in dev mode (import.meta.env.DEV): the server-side handler
   // defaults to prod-mode script generation which includes the WG block.
   // Pushing that to a dev hEX on the same LAN as the server would
   // accidentally provision a WG peer pointing at production VPS. In
   // dev, operator pastes manually as before (same path used today).
   const vlanInPlay =
      vlanAlreadyEnabled || (parentHasZones && vlanRef !== '');
   const applyMode = subVendoApplyMode(parentPlatform, vlanInPlay, import.meta.env.DEV);
   const canAutoPush = applyMode === 'push';
   const pushedRef = useRef(false);
   const [pushStatus, setPushStatus] = useState<'idle' | 'pushing' | 'success' | 'failed'>('idle');
   const [pushError, setPushError] = useState<string>('');
   const runPush = useCallback(async () => {
      setPushStatus('pushing');
      setPushError('');
      try {
         await applyRouterConfig(parent._id);
         setPushStatus('success');
         void useVendoDevicesStore.getState().fetch();
      } catch (err) {
         const msg = err instanceof Error ? err.message : String(err);
         console.error('[sub-vendo] auto-push failed:', err);
         setPushStatus('failed');
         setPushError(msg);
      }
   }, [parent._id]);
   useEffect(() => {
      if (step !== 3 || !registered || !canAutoPush || pushedRef.current) return;
      pushedRef.current = true;
      void runPush();
   }, [step, registered, canAutoPush, runPush]);

   // Mark the sub 'done' (→ nests under its parent, leaves "Setup in progress")
   // ONLY when setup is genuinely complete: VLAN subs need the push to have
   // SUCCEEDED; flat-LAN/dev subs are done on reaching the final step. A sub
   // closed midway, or whose push failed, stays != 'done' so it shows in "Setup
   // in progress" and can be resumed from the Devices page.
   const bumpedRef = useRef(false);
   useEffect(() => {
      if (!registered || bumpedRef.current) return;
      if (!isSubVendoSetupComplete(step === 3, canAutoPush, pushStatus)) return;
      bumpedRef.current = true;
      (async () => {
         try {
            await updateDeviceSetupStatusApi(registered.device._id, 'done');
            void useVendoDevicesStore.getState().fetch();
         } catch (err) {
            console.error('[sub-vendo] failed to bump setupStatus:', err);
         }
      })();
   }, [step, registered, canAutoPush, pushStatus]);

   // The success framing (green check, "ready", an enabled Done) must reflect
   // the ROUTER PUSH result — not just that registerDevice succeeded. While
   // pushing, Done is disabled; on failure we show a warning state + Retry so
   // the operator never closes on a half-applied sub-vendo thinking it's done.
   // Mapping lives in subVendoDoneView (pure, unit-tested).
   const doneView = subVendoDoneView(canAutoPush, pushStatus);
   const { pushPending, pushFailed, tone: doneTone } = doneView;

   const renderStep2 = () => (
      <>
         {registered ? (
            <>
               <PinMapEditor
                  deviceId={registered.device._id}
                  wired={connMode === 'ethernet'}
               />
               <NodeMcuFlasher
               offerFlashLater
                  deviceId={registered.device._id}
                  onSuccess={() => setStep(3)}
                  onSkip={() => setStep(3)}
               />
               {/* Always-visible escape hatch: NodeMcuFlasher only
                   surfaces its own Skip link when WebSerial is missing
                   or on error. Operators on Chrome who don't have the
                   NodeMCU physically plugged in right now still need
                   a way to finish the wizard — flashing can happen
                   later from device settings. Without this they're
                   stuck and the sub never bumps to 'done'. */}
               <SkipRow>
                  <SkipBtn type="button" onClick={() => setStep(3)}>
                     Skip flashing — finish later
                  </SkipBtn>
               </SkipRow>
            </>
         ) : (
            <FallbackNote>
               Device not registered — go back to Step 1.
            </FallbackNote>
         )}
      </>
   );

   const renderStep3 = () => (
      <DoneWrap>
         <DoneIcon $tone={doneTone}>{doneView.icon}</DoneIcon>
         <DoneTitle>{doneView.title}</DoneTitle>
         {vlanInPlay ? (
            canAutoPush ? (
               <>
                  <DoneBody>
                     <strong>{registered?.device.name}</strong> is registered
                     under <strong>{parent.name}</strong>. We&apos;re pushing
                     the updated VLAN config to your router now.
                  </DoneBody>
                  {pushStatus === 'pushing' && (
                     <PushStatus $tone="pending">Pushing router config…</PushStatus>
                  )}
                  {pushStatus === 'success' && (
                     <PushStatus $tone="success">
                        Router update started. The sub-vendo&apos;s VLAN network goes live in a
                        few minutes (longer on a big router); the main vendo&apos;s row shows the
                        new version when done.
                     </PushStatus>
                  )}
                  {pushStatus === 'failed' && (
                     <PushStatus $tone="error">
                        Auto-push failed: {pushError}
                        <br />
                        Open <strong>Vendo Admin → Devices</strong> and click
                        <strong> Update</strong> on the main vendo to retry.
                     </PushStatus>
                  )}
               </>
            ) : applyMode === 'pull' ? (
               <>
                  <DoneBody>
                     <strong>{registered?.device.name}</strong> is registered
                     under <strong>{parent.name}</strong>.
                  </DoneBody>
                  <PushStatus $tone="success">
                     Your router picks up the new network on its next check-in
                     (about a minute). Nothing to push, nothing to paste — check
                     the <strong>Networks</strong> page to see it confirmed.
                  </PushStatus>
               </>
            ) : (
               <>
                  <DoneBody>
                     <strong>{registered?.device.name}</strong> is registered
                     under <strong>{parent.name}</strong>.
                  </DoneBody>
                  <PushStatus $tone="pending">
                     <strong>Dev mode:</strong> auto-push is disabled to avoid
                     accidental prod WG peer creation. Re-fetch the parent&apos;s
                     setup script and paste into Winbox manually to apply the
                     new VLAN config.
                  </PushStatus>
               </>
            )
         ) : (
            <DoneBody>
               <strong>{registered?.device.name}</strong> is registered under{' '}
               <strong>{parent.name}</strong> and the NodeMCU is provisioned.
               It shares the parent&apos;s router and walled-garden config —
               no extra router setup needed.
            </DoneBody>
         )}
         <DoneNote>
            Plug the NodeMCU into the coin acceptor at the new vendo location.
            Heartbeat will register the device as online within a minute.
         </DoneNote>
         <BtnRow>
            {pushFailed ? (
               <>
                  <div onClick={() => void runPush()}>
                     <Btn
                        title="Retry push"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
                  <div onClick={handleClose}>
                     <Btn
                        title="Close"
                        type="outline"
                        size="small"
                        textcolor={colors.MAIN_DARK}
                     />
                  </div>
               </>
            ) : (
               <div onClick={pushPending ? undefined : handleClose}>
                  <Btn
                     title={pushPending ? 'Pushing…' : 'Done'}
                     type={pushPending ? 'disabled' : 'normal'}
                     size="small"
                     bgcolor={pushPending ? colors.GRAY10 : colors.PRIMARY}
                     textcolor={pushPending ? colors.GRAY1 : colors.WHITE}
                  />
               </div>
            )}
         </BtnRow>
      </DoneWrap>
   );

   const wrapperWide = step === 1 && showVlanColumn;

   return (
      <Wrapper $wide={wrapperWide}>
         <Stepper>
            {SUB_STEPS.map((s, i) => (
               <React.Fragment key={s.num}>
                  <Bullet $active={s.num === step} $done={s.num < step}>
                     {s.num < step ? '✓' : s.num}
                  </Bullet>
                  {i < SUB_STEPS.length - 1 && (
                     <Connector $done={s.num < step} />
                  )}
               </React.Fragment>
            ))}
         </Stepper>
         <StepLabel>
            Step {step} of {SUB_STEPS.length} —{' '}
            <strong>{SUB_STEPS.find((s) => s.num === step)?.label}</strong>
         </StepLabel>

         {step === 1 && renderStep1()}
         {step === 2 && renderStep2()}
         {step === 3 && renderStep3()}
      </Wrapper>
   );
};

const Wrapper = styled.div<{ $wide?: boolean }>`
   width: ${({ $wide }) => ($wide ? '860px' : '480px')};
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 14px;
   transition: width 0.18s ease;
   @media (max-width: 900px) { width: 100%; }
`;

const Step1Body = styled.div<{ $twoCol: boolean }>`
   display: grid;
   grid-template-columns: ${({ $twoCol }) => ($twoCol ? '1fr 1fr' : '1fr')};
   gap: 14px;
   align-items: start;
   @media (max-width: 768px) {
      grid-template-columns: 1fr;
   }
`;

const ColLeft = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   min-width: 0;
`;

const ColRight = styled.div`
   display: flex;
   flex-direction: column;
   gap: 14px;
   min-width: 0;
`;

const ParentBanner = styled.div`
   padding: 12px 14px;
   border-radius: 10px;
   background: ${colors.PRIMARY}10;
   border: 1px solid ${colors.PRIMARY}25;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const BannerLabel = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.PRIMARY};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const BannerValue = styled.p`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ParentId = styled.span`
   font-family: monospace;
   font-size: 11px;
   font-weight: 400;
   color: ${colors.GRAY5};
`;

const BannerMeta = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

/* Advanced disclosure — same pattern as DeviceRegister.tsx. */

const AdvancedToggle = styled.button`
   align-self: stretch;
   background: ${colors.PRIMARY}10;
   border: 1px solid ${colors.PRIMARY}40;
   color: ${colors.PRIMARY};
   padding: 8px 14px;
   border-radius: 8px;
   font-size: 13px;
   font-weight: 700;
   cursor: pointer;
   font-family: inherit;
   text-align: left;
   display: flex;
   align-items: center;
   justify-content: space-between;
   &:hover {
      background: ${colors.PRIMARY}18;
      border-color: ${colors.PRIMARY};
   }
`;

const AdvancedPane = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const AdvancedNote = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

/* Credits + license-choice UI — mirrors DeviceRegister.tsx's pattern so
   sub-vendo and main-vendo registration share the same credit flow. */

const LicenseSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const SectionLabel = styled.p`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const ChoiceCard = styled.button<{ $selected: boolean; $static?: boolean }>`
   background: ${({ $selected }) => ($selected ? `${colors.PRIMARY}08` : colors.WHITE)};
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 10px 12px;
   cursor: ${({ $static }) => ($static ? 'default' : 'pointer')};
   text-align: left;
   font-family: inherit;
   display: flex;
   flex-direction: column;
   gap: 4px;
   transition: all 0.15s;
   position: relative;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const AutoRenewRow = styled.div`
   display: flex;
   gap: 10px;
   padding: 10px;
   background: ${colors.WHITE};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const AutoRenewCheckbox = styled.input`
   width: 16px;
   height: 16px;
   margin-top: 2px;
   accent-color: ${colors.PRIMARY};
   cursor: pointer;
`;

const AutoRenewLabel = styled.label`
   display: flex;
   flex-direction: column;
   gap: 2px;
   cursor: pointer;
   flex: 1;
`;

const AutoRenewTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const AutoRenewDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const BuyCreditsCard = styled.button`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px;
   border: 1px dashed ${colors.PRIMARY};
   background: ${colors.PRIMARY}08;
   border-radius: 10px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   transition: background 0.15s;
   &:hover { background: ${colors.PRIMARY}12; }
`;

const BuyCreditsIcon = styled.span`
   font-size: 18px;
`;

const BuyCreditsText = styled.span`
   display: flex;
   flex-direction: column;
   gap: 2px;
   flex: 1;
`;

const BuyCreditsTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const BuyCreditsDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const BuyCreditsArrow = styled.span`
   font-size: 18px;
   color: ${colors.PRIMARY};
   font-weight: 700;
`;

const ChoiceIcon = styled.span`
   font-size: 20px;
`;

const ChoiceTitle = styled.span`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const ChoiceDesc = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const ChoiceTag = styled.span<{ $green?: boolean }>`
   position: absolute;
   top: 8px;
   right: 8px;
   font-size: 9px;
   font-weight: 700;
   padding: 2px 7px;
   border-radius: 10px;
   background: ${({ $green }) => ($green ? `${colors.SUCCESS}15` : colors.GRAY14)};
   color: ${({ $green }) => ($green ? colors.SUCCESS : colors.GRAY3)};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const Note = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 10px 12px;
   background: ${colors.GRAY14};
   border-radius: 8px;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

const Stepper = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 4px 0 2px;
`;

const Bullet = styled.div<{ $active: boolean; $done: boolean }>`
   width: 24px;
   height: 24px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 12px;
   font-weight: 700;
   background: ${({ $active, $done }) =>
      $done ? colors.PRIMARY : $active ? colors.PRIMARY : colors.GRAY13};
   color: ${({ $active, $done }) =>
      $done || $active ? colors.WHITE : colors.GRAY5};
   transition: background 0.2s;
`;

const Connector = styled.div<{ $done: boolean }>`
   flex: 1;
   height: 2px;
   background: ${({ $done }) => ($done ? colors.PRIMARY : colors.GRAY13)};
   transition: background 0.2s;
`;

const StepLabel = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   text-align: center;
   strong {
      color: ${colors.MAIN_DARK};
   }
`;

const FallbackNote = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   text-align: center;
   padding: 20px;
`;

const SkipRow = styled.div`
   display: flex;
   justify-content: center;
   margin-top: 12px;
`;

const SkipBtn = styled.button`
   background: none;
   border: none;
   font-size: 13px;
   color: ${colors.GRAY5};
   text-decoration: underline;
   cursor: pointer;
   padding: 6px 12px;
   &:hover {
      color: ${colors.PRIMARY};
   }
`;

const DoneWrap = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 12px;
   padding: 20px 0;
`;

const DoneIcon = styled.div<{ $tone?: 'pending' | 'success' | 'error' }>`
   width: 56px;
   height: 56px;
   border-radius: 50%;
   background: ${(p) =>
      p.$tone === 'error'
         ? '#ffebee'
         : p.$tone === 'pending'
         ? '#e3f2fd'
         : `${colors.SUCCESS}20`};
   color: ${(p) =>
      p.$tone === 'error'
         ? '#c62828'
         : p.$tone === 'pending'
         ? '#1565c0'
         : colors.SUCCESS};
   display: grid;
   place-items: center;
   font-size: 28px;
   font-weight: 700;
`;

const DoneTitle = styled.h3`
   font-size: 17px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin: 0;
`;

const DoneBody = styled.p`
   font-size: 13px;
   color: ${colors.GRAY4};
   line-height: 1.5;
   margin: 0;
   max-width: 380px;
`;

const DoneNote = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   font-style: italic;
   margin: 0;
   max-width: 380px;
`;

const PushStatus = styled.div<{ $tone: 'pending' | 'success' | 'error' }>`
   font-size: 13px;
   padding: 10px 14px;
   border-radius: 6px;
   max-width: 420px;
   line-height: 1.5;
   background: ${(p) =>
      p.$tone === 'success'
         ? '#e8f5e9'
         : p.$tone === 'error'
         ? '#ffebee'
         : '#e3f2fd'};
   color: ${(p) =>
      p.$tone === 'success'
         ? '#2e7d32'
         : p.$tone === 'error'
         ? '#c62828'
         : '#1565c0'};
`;

const ComingSoonWrap = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   text-align: center;
   gap: 14px;
   padding: 32px 20px;
   max-width: 420px;
   margin: 0 auto;
`;

const ComingSoonBadge = styled.span`
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   color: ${colors.PRIMARY};
   background: ${colors.PRIMARY20};
   border: 1px solid ${colors.PRIMARY50};
   padding: 4px 10px;
   border-radius: 999px;
`;

const ComingSoonTitle = styled.h3`
   font-size: 17px;
   font-weight: 700;
   color: ${colors.MAIN_DARK90};
   margin: 0;
`;

const ComingSoonBody = styled.p`
   font-size: 13px;
   color: ${colors.GRAY4};
   line-height: 1.55;
   margin: 0;
`;

const ComingSoonActions = styled.div`
   margin-top: 8px;
`;

const VlanSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 12px 14px;
   background: ${colors.PRIMARY}08;
   border: 1px solid ${colors.PRIMARY}25;
   border-radius: 10px;
`;

const VlanLabel = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const VlanHelp = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   strong { color: ${colors.MAIN_DARK}; }
`;

const VlanChoiceRow = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 8px;
`;

const VlanChoiceBtn = styled.button<{ $selected: boolean }>`
   background: ${({ $selected }) => ($selected ? colors.PRIMARY : colors.WHITE)};
   color: ${({ $selected }) => ($selected ? colors.WHITE : colors.MAIN_DARK)};
   border: 2px solid ${({ $selected }) => ($selected ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 8px;
   padding: 8px 12px;
   cursor: pointer;
   font-family: inherit;
   font-size: 13px;
   font-weight: 700;
   transition: all 0.15s;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const VlanFields = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 12px 14px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const VlanFieldsTitle = styled.p`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   display: flex;
   align-items: center;
   gap: 8px;
`;

const VlanLockedBadge = styled.span`
   font-size: 9px;
   font-weight: 700;
   padding: 2px 6px;
   border-radius: 6px;
   background: ${colors.GRAY13};
   color: ${colors.GRAY3};
   text-transform: none;
   letter-spacing: 0;
`;

const FieldLabel = styled.label`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY3};
`;

const Select = styled.select<{ $error?: boolean }>`
   width: 100%;
   padding: 8px 10px;
   border: 1px solid ${({ $error }) => ($error ? colors.WARNING : colors.GRAY12)};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   cursor: pointer;
`;

const VlanSwitchNotice = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   padding: 8px 10px;
   background: ${colors.WHITE};
   border: 1px dashed ${colors.GRAY12};
   border-radius: 6px;
   strong { color: ${colors.MAIN_DARK}; }
`;

export default SubVendoAdd;

/* ---- VLAN zone card: create when none is found (2026-10-15) ---- */
const OfflineNotice = styled.p`
   font-size: 12px;
   line-height: 1.5;
   margin: 0;
   padding: 10px 12px;
   border-radius: 8px;
   color: ${colors.DANGER};
   background: ${colors.DANGER}12;
   border: 1px solid ${colors.DANGER}40;
`;

const ChosenVlan = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY10};
   font-size: 13px;
   color: ${colors.MAIN_DARK};
`;

const ZoneStatus = styled.span<{ $status: 'pending' | 'live' | 'failed' }>`
   font-weight: 700;
   color: ${({ $status }) =>
      $status === 'live' ? colors.SUCCESS : $status === 'failed' ? colors.DANGER : colors.GRAY1};
`;

const NewVlanRow = styled.div`
   display: grid;
   grid-template-columns: 1fr auto;
   gap: 10px;
   align-items: end;
`;

const ErrText = styled.p`
   font-size: 12px;
   color: ${colors.DANGER};
   margin: 0;
`;

const LinkBtn = styled.button`
   align-self: flex-start;
   background: none;
   border: none;
   padding: 0;
   margin-top: 4px;
   color: ${colors.PRIMARY};
   font-weight: 700;
   font-size: 12px;
   cursor: pointer;
   text-decoration: underline;
`;
